import { RefreshCwIcon, TriangleAlertIcon } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { DeckGrid, DeckGridSkeleton } from "@/features/decks/deck-grid";
import { useManifest, useSyncData } from "@/lib/data/queries";
import { cn } from "@/lib/utils";

import { MainHeader } from "./main-tabs";

/** The deck overview. Renders entirely from the manifest. */
export function OverviewPage() {
  const manifest = useManifest();
  const sync = useSyncData();

  const handleSync = () => {
    sync.mutate(undefined, {
      onSuccess: () => toast.success("Decks are up to date"),
      onError: (error) =>
        toast.error("Could not refresh decks", {
          description: error.message,
        }),
    });
  };

  const decks = manifest.data?.decks;

  return (
    <div className="flex flex-col gap-6">
      <MainHeader
        action={
          <Button
            variant="ghost"
            size="icon-lg"
            aria-label="Refresh decks"
            disabled={sync.isPending}
            onClick={handleSync}
          >
            <RefreshCwIcon className={cn(sync.isPending && "animate-spin")} />
          </Button>
        }
      />

      {decks ? (
        decks.length > 0 ? (
          <DeckGrid decks={decks} />
        ) : (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No decks published yet.
          </p>
        )
      ) : manifest.isPending ? (
        <DeckGridSkeleton />
      ) : (
        <ManifestError
          message={
            manifest.error instanceof Error
              ? manifest.error.message
              : "The deck list could not be loaded."
          }
          isRetrying={sync.isPending}
          onRetry={handleSync}
        />
      )}
    </div>
  );
}

interface ManifestErrorProps {
  message: string;
  isRetrying: boolean;
  onRetry: () => void;
}

function ManifestError({ message, isRetrying, onRetry }: ManifestErrorProps) {
  return (
    <div className="flex flex-col items-center gap-4 py-12 text-center">
      <TriangleAlertIcon className="size-8 text-destructive" />
      <div className="flex flex-col gap-1">
        <p className="font-medium">Could not load your decks</p>
        <p className="max-w-md text-sm break-words text-muted-foreground">
          {message}
        </p>
      </div>
      <Button
        size="lg"
        className="h-11"
        disabled={isRetrying}
        onClick={onRetry}
      >
        <RefreshCwIcon className={cn(isRetrying && "animate-spin")} />
        Try again
      </Button>
    </div>
  );
}
